import { normalizeExperienceEventNoSpaces } from './experience-keys';

const EXPERIENCE_IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp'];

const getFilenameParts = (filename) => {
  const lastDotIndex = filename.lastIndexOf('.');

  if (lastDotIndex < 1) {
    return null;
  }

  return {
    name: filename.slice(0, lastDotIndex),
    extension: filename.slice(lastDotIndex + 1).toLowerCase(),
  };
};

const isExperienceImageExtension = (extension) => {
  return EXPERIENCE_IMAGE_EXTENSIONS.includes(extension);
};

const parseExperienceImageName = (filename, prefix) => {
  const filenameParts = getFilenameParts(filename);

  if (!filenameParts || !isExperienceImageExtension(filenameParts.extension)) {
    return null;
  }

  const nameParts = filenameParts.name.split('-');

  if (nameParts.length > 2) {
    return null;
  }

  const [blockName, imageOrderRaw] = nameParts;

  if (!blockName.startsWith(prefix)) {
    return null;
  }

  const groupOrder = Number(blockName.slice(prefix.length));
  const imageOrder = imageOrderRaw ? Number(imageOrderRaw) : 0;

  if (!Number.isInteger(groupOrder) || groupOrder < 1 || !Number.isInteger(imageOrder) || imageOrder < 0) {
    return null;
  }

  return {
    groupOrder,
    imageOrder,
  };
};

const createExperienceImageNameParser = (prefix) => {
  return (filename) => parseExperienceImageName(filename, prefix);
};

const getFolderPrefix = (candidate) => {
  const folderCandidate = normalizeExperienceEventNoSpaces(candidate).toLowerCase();

  return `./${folderCandidate}/`;
};

const getOrderedMatchesInFolder = (assetKeys, folderPrefix, parseFilename) => {
  const matches = [];

  for (const assetPath of assetKeys) {
    if (!assetPath.startsWith(folderPrefix)) {
      continue;
    }

    const filename = assetPath.slice(folderPrefix.length);
    const parsedFilename = parseFilename(filename);

    if (!parsedFilename || parsedFilename.imageOrder !== 0) {
      continue;
    }

    matches.push({
      assetPath,
      order: parsedFilename.groupOrder,
    });
  }

  return [...matches].sort((matchA, matchB) => matchA.order - matchB.order);
};

const addAssetToGroup = (groups, groupOrder, asset) => {
  const group = groups.get(groupOrder);

  if (group) {
    group.push(asset);
    return;
  }

  groups.set(groupOrder, [asset]);
};

const resolveAssetGroups = (groups, requireContext) => {
  return [...groups.entries()]
    .sort(([orderA], [orderB]) => orderA - orderB)
    .map(([order, assets]) => ({
      order,
      images: [...assets]
        .sort((assetA, assetB) => assetA.order - assetB.order)
        .map(({ assetPath }) => requireContext(assetPath)),
    }));
};

const getGroupedMatchesInFolder = (assetKeys, folderPrefix, parseFilename) => {
  const groups = new Map();

  for (const assetPath of assetKeys) {
    if (!assetPath.startsWith(folderPrefix)) {
      continue;
    }

    const filename = assetPath.slice(folderPrefix.length);
    const parsedFilename = parseFilename(filename);

    if (!parsedFilename) {
      continue;
    }

    addAssetToGroup(groups, parsedFilename.groupOrder, {
      assetPath,
      order: parsedFilename.imageOrder,
    });
  }

  return groups;
};

const getExperienceAssetGroupsByPrefix = (requireContext, eventCandidates, prefix) => {
  const assetKeys = requireContext.keys();
  const parseFilename = createExperienceImageNameParser(prefix);

  for (const candidate of eventCandidates) {
    const folderPrefix = getFolderPrefix(candidate);
    const groups = getGroupedMatchesInFolder(assetKeys, folderPrefix, parseFilename);

    if (groups.size > 0) {
      return resolveAssetGroups(groups, requireContext);
    }
  }

  return [];
};

const getExperienceAssetsByPrefix = (requireContext, eventCandidates, prefix) => {
  const assetKeys = requireContext.keys();
  const parseFilename = createExperienceImageNameParser(prefix);

  for (const candidate of eventCandidates) {
    const folderPrefix = getFolderPrefix(candidate);

    const matches = getOrderedMatchesInFolder(assetKeys, folderPrefix, parseFilename).map(
      ({ assetPath }) => requireContext(assetPath)
    );

    if (matches.length > 0) {
      return matches;
    }
  }

  return [];
};

const tryRequireExperienceAsset = (requireContext, eventCandidates, suffix) => {
  const assetKeys = new Set(requireContext.keys());

  for (const candidate of eventCandidates) {
    const folderPrefix = getFolderPrefix(candidate);

    for (const extension of EXPERIENCE_IMAGE_EXTENSIONS) {
      const assetPath = `${folderPrefix}${suffix}.${extension}`;

      if (assetKeys.has(assetPath)) {
        return requireContext(assetPath);
      }
    }
  }

  return null;
};

export {
  getExperienceAssetGroupsByPrefix,
  getExperienceAssetsByPrefix,
  tryRequireExperienceAsset,
};
